/** @format */
import {useState, ChangeEvent, DragEvent} from 'react';

import {CloudUploadOutlined} from '@mui/icons-material';

import {CircularProgress} from '@mui/material';

import {Control, useController} from 'react-hook-form';

import CloseIcon from '@mui/icons-material/Close';

import {SimpleButton} from 'components/buttons/SimpleButton';

import {FormValues} from '../../types';
import {fileToBase64} from './helpers/fileToBase64';
import {
  Dropzone,
  ErrorText,
  RemoveButton,
  ResultBox,
  Root,
  ScreenshotBorderBox,
  ScreenshotImg,
} from './styles';

type Props = {
  control: Control<FormValues>;
  disabled: boolean;
  maxFiles: number;
  maxSizeMb: number;
};

const ScreenshotUploader = ({disabled, maxFiles, maxSizeMb, control}: Props) => {
  const {
    field: {value, onChange},
  } = useController({name: 'screenshots', control});

  const [isUploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const canAddMore = value.length < maxFiles;

  const clearUploadError = () => {
    setUploadError(null);
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (!files || files.length === 0) {
      return;
    }

    if (value.length + files.length > maxFiles) {
      setUploadError(`Можно передать не более ${maxFiles} изображений`);
      return;
    }

    const asArray = Array.from(files).filter(f => f.type.startsWith('image/'));
    const sizeLimit = maxSizeMb * 1024 * 1024;

    if (asArray.some(f => f.size > sizeLimit)) {
      setUploadError(`Максимальный размер файла ${maxSizeMb} Mb`);
      return;
    }

    setUploading(true);

    try {
      const base64List = await Promise.all(asArray.map(fileToBase64));
      onChange([...value, ...base64List]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setUploadError('Ошибка загрузки изображения');
    } finally {
      setUploading(false);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length) {
      void handleFiles(files);
      // Сбросим value, чтобы можно было выбрать тот же файл снова
      e.currentTarget.value = '';
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    clearUploadError();
    if (disabled) {
      return;
    }
    const dt = e.dataTransfer;
    if (dt?.files?.length) {
      void handleFiles(dt.files);
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleRemove = (index: number) => {
    const next = value.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <Root>
      {value.length > 0 && (
        <ResultBox>
          {value.map((src, index) => (
            <ScreenshotBorderBox key={`screenshot-${index}`}>
              <ScreenshotImg src={src} alt='' />
              <RemoveButton onClick={() => handleRemove(index)}>
                <CloseIcon />
              </RemoveButton>
            </ScreenshotBorderBox>
          ))}
        </ResultBox>
      )}

      <Dropzone
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        dragOver={dragOver}
      >
        <SimpleButton
          disabled={disabled || isUploading || !canAddMore}
          component='label'
          onClick={clearUploadError}
        >
          {isUploading ? (
            <>
              <CircularProgress size={16} />
              загружается...
            </>
          ) : (
            <>
              <CloudUploadOutlined />
              {canAddMore ? 'Загрузить скриншот(ы)' : 'Достигнут максимум скриншотов'}
            </>
          )}
          <input hidden type='file' accept='image/*' multiple onChange={onInputChange} />
        </SimpleButton>
      </Dropzone>

      {uploadError ? <ErrorText>{uploadError}</ErrorText> : null}
    </Root>
  );
};

export {ScreenshotUploader};
